import { useCallback, useEffect, useRef } from 'react';

import { ISearchUser } from '@/features/user/store/user.model';
import { searchUsersAgent } from '@/features/user/store/user.actions';
import { AllUsersRows } from '@/features/user/components/allUsers/AllUsersRows';
import { useAppDispatch, useAppSelector } from '@/hooks/redux-helpers';
import { AllUsersHeader } from '@/features/user/components/allUsers/AllUsersHeader';
import { IUsersFiltersForm } from '@/features/user/store/allUsers.types';

import { useAllUsersFilters, parseAllUsersFiltersValue } from './useAllUsersFilters';

export function useAllUsersTable(defaultFilters: IUsersFiltersForm = {}) {
  const dispatch = useAppDispatch();

  const users = useAppSelector((state) => state.users.paginateUsers.elements);
  const pagination = useAppSelector((state) => state.users.paginateUsers.pagination);
  const isLoading = useAppSelector((state) => state.users.isLoading);
  const error = useAppSelector((state) => state.users.error);

  const headers = AllUsersHeader();
  const rows = AllUsersRows({ users, isLoading });

  const queryParameters = useRef<{ page: number; filters: ISearchUser }>({
    page: undefined,
    filters: Object.keys(defaultFilters).reduce((prev, current) => {
      return {
        ...prev,
        ...parseAllUsersFiltersValue(current as keyof IUsersFiltersForm, defaultFilters[current]),
      };
    }, {}),
  });

  const filtersDeps = JSON.stringify(defaultFilters);

  useEffect(() => {
    const filters = JSON.parse(filtersDeps);
    queryParameters.current = {
      page: 1,
      filters: Object.keys(filters).reduce((prev, current) => {
        return {
          ...prev,
          ...parseAllUsersFiltersValue(current as keyof IUsersFiltersForm, filters[current]),
        };
      }, {}),
    };
    dispatch(searchUsersAgent(1, queryParameters.current.filters, true));
  }, [dispatch, filtersDeps]);

  const onPageChange = (page: number) => {
    queryParameters.current = {
      ...queryParameters.current,
      page,
    };
    const { filters, ...parameters } = queryParameters.current;
    dispatch(searchUsersAgent(parameters.page, filters, true));
  };

  const handleFilterChange = useCallback(
    (filtersParameters: ISearchUser) => {
      queryParameters.current = {
        ...queryParameters.current,
        filters: filtersParameters,
        page: 1,
      };
      const { filters, ...parameters } = queryParameters.current;
      dispatch(searchUsersAgent(parameters.page, filters, true));
    },
    [dispatch],
  );

  const { searchField, userStatusField, createdDateField, usTaxResidentField, clearFilters, hasFilters } =
    useAllUsersFilters({
      onChange: handleFilterChange,
      initialValues: defaultFilters,
    });

  const onFilterChange = useCallback(
    (queryParameters: ISearchUser) => {
      handleFilterChange(queryParameters);
      if (!queryParameters) {
        clearFilters();
      }
    },
    [clearFilters, handleFilterChange],
  );

  return {
    users,
    headers,
    pagination,
    error,
    rows,
    onPageChange,
    onFilterChange,
    hasFilters,
    clearFilters,
    fields: {
      search: searchField,
      userStatus: userStatusField,
      createdDate: createdDateField,
      usTaxResident: usTaxResidentField,
    },
  };
}
